import { ref, computed, ComputedRef, Ref } from 'vue'
import { useStore } from '@/store'
import { BookingSummary } from '@/services/BookingService.interface'
import { Lihe } from '@/services/CatalogService.interface'
import BookingDataMapper from '@/mappers/BookingDataMapper'
import { extractUniqueValues } from '@/utils'
import { useError } from '@/composables/useError'
import { ModalId } from '@/config/modalIds'

interface BookingsMap {
  [key: string]: ComputedRef<BookingSummary[]>
}

interface UpdatedBookingsMap {
  [key: string]: Ref<BookingSummary[]>
}

const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms))

const waitUntil = async (
  predicate: () => boolean,
  timeoutMs: number,
  intervalMs = 100
): Promise<boolean> => {
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    if (predicate()) return true
    await sleep(intervalMs)
  }
  return predicate()
}

const findBookingByReference = (
  updatedBookings: UpdatedBookingsMap,
  reference: string
): BookingSummary | undefined => {
  const bookings = Object.values(updatedBookings).flatMap(group => group.value)
  return bookings.find((booking: BookingSummary) => booking.reference === reference)
}

export function useBookings(bookingsMap: BookingsMap): {
  updatedBookings: UpdatedBookingsMap
  fetchAndMapBookings: () => Promise<void>
  isLoading: ComputedRef<boolean>
  error: ComputedRef<string | null>
  openPanelByRef: () => Promise<void>
} {
  const store = useStore()
  const error = useError(store)

  // Create a reactive object to hold updated bookings
  const updatedBookings: UpdatedBookingsMap = {}
  Object.keys(bookingsMap).forEach(key => {
    updatedBookings[key] = ref<BookingSummary[]>([])
  })

  const userEmail: ComputedRef<string | undefined> = computed(
    () => store.state.auth?.user?.email
  )

  const lihes: ComputedRef<Lihe[]> = computed(
    () => store.getters['catalog/getLihes']
  )

  const isLoading = computed(() => {
    return (
      store.getters['booking/isLoading']('fetchBookings') ||
      store.getters['catalog/isLoading']('fetchLihes')
    )
  })

  // Function to fetch and map bookings
  const fetchAndMapBookings = async (): Promise<void> => {
    if (!userEmail.value) {
      console.error('User ID is missing. Ensure user is authenticated.')
      return
    }

    try {
      await store.dispatch('booking/fetchBookings', {
        userEmail: userEmail.value,
      })

      // Combine all booking arrays into a single array
      const allBookings: BookingSummary[] = Object.values(bookingsMap).flatMap(
        b => b.value
      )
      const liheIds: number[] = extractUniqueValues(allBookings, 'liheId')

      if (liheIds.length > 0) {
        await store.dispatch('catalog/fetchLihes', liheIds)
      }

      // Assign images and overwrite prestation names for each bookings array
      Object.entries(bookingsMap).forEach(([key, bookingsRef]) => {
        const bookingsWithImages = BookingDataMapper.assignLiheImages(
          bookingsRef.value,
          lihes.value || []
        )
        const bookingsWithUpdatedNames = BookingDataMapper.overwritePrestationName(
          bookingsWithImages,
          lihes.value || []
        )
        updatedBookings[key].value = bookingsWithUpdatedNames
      })

    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  // Funnel links may put `#opened[]=<ref>` directly on the account URL.
  // Mirror the redirect bridge: move it into per-tab sessionStorage and clean the hash.
  const consumeOpenedRefFromHash = (): void => {
    if (typeof window === 'undefined' || !window.location.hash) return

    try {
      const hashParams = new URLSearchParams(window.location.hash.slice(1))
      const openedRef = hashParams.get('opened[]')
      if (!openedRef) return

      sessionStorage.setItem('pendingPanelOpen', openedRef)
      hashParams.delete('opened[]')

      const remainingHash = hashParams.toString()
      const nextUrl =
        window.location.pathname +
        window.location.search +
        (remainingHash ? `#${remainingHash}` : '')
      window.history.replaceState(null, '', nextUrl)
    } catch (error) {
      console.error('Error reading opened[] from hash:', error)
    }
  }

  // Function to open panel by reference.
  // The reference is provided via per-tab sessionStorage key `pendingPanelOpen`
  // (written by `redirect/customer-account-redirect.html`, funnel hash, or post-login).
  const openPanelByRef = async (): Promise<void> => {
    consumeOpenedRefFromHash()

    const pendingRef = sessionStorage.getItem('pendingPanelOpen')
    if (!pendingRef) return

    sessionStorage.removeItem('pendingPanelOpen')

    const bookingAvailable = await waitUntil(
      () => !!findBookingByReference(updatedBookings, pendingRef),
      5000
    )

    if (!bookingAvailable) return

    const booking = findBookingByReference(updatedBookings, pendingRef)
    if (booking) {
      store.dispatch('booking/selectBooking', booking)
      store.commit('modal/open', ModalId.BOOKING_PANEL)
    }
  }

  return {
    updatedBookings,
    fetchAndMapBookings,
    isLoading,
    error,
    openPanelByRef,
  }
}
